// Client-side image processing: resize + convert to base64 data URLs

const MAX_DIMENSION = 1400;
const JPEG_QUALITY = 0.85;
const MAX_FILE_BYTES = 8 * 1024 * 1024; // 8MB cap on raw input

export async function fileToCompressedDataUrl(file) {
  if (!file.type.startsWith('image/')) {
    throw new Error('Only image files are allowed');
  }
  if (file.size > MAX_FILE_BYTES) {
    throw new Error('Image too large (max 8MB).');
  }

  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('File read failed'));
    reader.readAsDataURL(file);
  });

  // Skip resizing for SVG / GIF — pass through as-is
  if (file.type === 'image/svg+xml' || file.type === 'image/gif') {
    return dataUrl;
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
        if (width >= height) {
          height = Math.round((height * MAX_DIMENSION) / width);
          width = MAX_DIMENSION;
        } else {
          width = Math.round((width * MAX_DIMENSION) / height);
          height = MAX_DIMENSION;
        }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      // Force jpeg output for compression (preserves PNG transparency lost, acceptable for product photos)
      try {
        resolve(canvas.toDataURL('image/jpeg', JPEG_QUALITY));
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = () => reject(new Error('Image decode failed'));
    img.src = dataUrl;
  });
}

export async function filesToCompressedDataUrls(fileList) {
  const files = Array.from(fileList);
  const out = [];
  for (const f of files) {
    try {
      out.push(await fileToCompressedDataUrl(f));
    } catch (e) {
      console.error(`Skipped ${f.name}:`, e.message);
    }
  }
  return out;
}

// Higher-resolution variant for hero banner uploads (max 2200px wide).
export async function fileToBannerDataUrl(file) {
  if (!file.type.startsWith('image/')) throw new Error('Only image files are allowed');
  if (file.size > 12 * 1024 * 1024) throw new Error('Banner image too large (max 12MB).');

  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('File read failed'));
    reader.readAsDataURL(file);
  });

  if (file.type === 'image/svg+xml' || file.type === 'image/gif') return dataUrl;

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      const MAX = 2200;
      if (width > MAX || height > MAX) {
        if (width >= height) {
          height = Math.round((height * MAX) / width);
          width = MAX;
        } else {
          width = Math.round((width * MAX) / height);
          height = MAX;
        }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      try { resolve(canvas.toDataURL('image/jpeg', 0.86)); }
      catch (e) { reject(e); }
    };
    img.onerror = () => reject(new Error('Image decode failed'));
    img.src = dataUrl;
  });
}
